import React, { useState } from 'react';

import _ from 'lodash';
import { Settings } from '@mui/icons-material';
import {
  Alert,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Popover,
  Snackbar,
  Switch,
  Tooltip,
} from '@mui/material';

import { Analytics } from './analytics/Analytics';
import { AnalyticsEvent } from './analytics/AnalyticsEvent';

export interface AppSettings {
  disableBuiltInSearch: boolean | undefined;

  filterCaseSensitive: boolean | undefined;
  filterRegex: boolean | undefined;

  searchCaseSensitive: boolean | undefined;
  searchRegex: boolean | undefined;

  enableTimelineV2: boolean | undefined;
  enableMap: boolean | undefined;
  showBookmarks: boolean | undefined;

  disableAnalytics: boolean | undefined;

  /**
   * When enabled, the latest "Bug Report Button Tap" event is automatically
   * used as the time anchor (base time offset) for all Δ time values.
   */
  autoBugButtonTimeReference: boolean | undefined;
}

export class AppSettingsManager {
  settings: AppSettings = {
    disableBuiltInSearch: true,
    enableTimelineV2: true,
    filterCaseSensitive: false,
    filterRegex: false,
    searchCaseSensitive: false,
    searchRegex: false,
    showBookmarks: false,
    disableAnalytics: false,
    autoBugButtonTimeReference: false,
    enableMap: true,
  };

  onChange?: () => void;

  load() {
    const value = localStorage.getItem('boltMobileLogViewerSettings');
    if (!value) {
      return;
    }

    try {
      const settingsJson = JSON.parse(value);
      const newSettings = _.cloneDeep(this.settings);
      _.merge(newSettings, settingsJson);
      this.settings = newSettings;
    } catch (error) {
      console.error(`Error during parsing settings`, error);
    }
  }

  save() {
    const value = JSON.stringify(this.settings);
    localStorage.setItem('boltMobileLogViewerSettings', value);

    console.log('Save settings', this.settings);

    if (this.onChange) {
      this.onChange();
    }
  }
}

interface AppSettingsProps {
  settingsManager: AppSettingsManager;
  onNoBugButtonFound?: () => void;
}

export function AppSettingsDiv({ settingsManager }: AppSettingsProps) {
  const [toastOpen, setToastOpen] = useState(false);

  const updateSettings = (update: Partial<AppSettings>) => {
    const newSettings = _.cloneDeep(settingsManager.settings);
    _.merge(newSettings, update);
    settingsManager.settings = newSettings;
    settingsManager.save();
  };

  return (
    <div>
      <FormGroup>
        <Tooltip
          disableInteractive
          title="Disable triggering in-browser search on Cmd+F even if custom search text field is focused"
        >
          <FormControlLabel
            label="Disable in-browser search"
            control={
              <Switch
                checked={settingsManager.settings.disableBuiltInSearch}
                onChange={(e) => {
                  updateSettings({ disableBuiltInSearch: e.target.checked });
                }}
              />
            }
          />
        </Tooltip>

        <Tooltip disableInteractive title="Enable timeline">
          <FormControlLabel
            label="Enable timeline"
            control={
              <Switch
                checked={settingsManager.settings.enableTimelineV2}
                onChange={(e) => {
                  if (!e.target.checked) {
                    Analytics.log(AnalyticsEvent.TimelineHidden);
                  }
                  updateSettings({ enableTimelineV2: e.target.checked });
                }}
              />
            }
          />
        </Tooltip>

        <Tooltip disableInteractive title="Enable map">
          <FormControlLabel
            label="Enable map"
            control={
              <Switch
                checked={settingsManager.settings.enableMap}
                onChange={(e) => {
                  updateSettings({ enableMap: e.target.checked });
                }}
              />
            }
          />
        </Tooltip>

        <Tooltip
          disableInteractive
          title="Automatically use the latest 'Bug Report Button Tap' event as the time reference point for all Δ time values"
        >
          <FormControlLabel
            label="Auto bug button time reference"
            control={
              <Switch
                checked={settingsManager.settings.autoBugButtonTimeReference}
                onChange={(e) => {
                  updateSettings({ autoBugButtonTimeReference: e.target.checked });
                }}
              />
            }
          />
        </Tooltip>
      </FormGroup>

      <div style={{ marginTop: 16 }}>
        <div>Shortcuts:</div>
        <table style={{ width: '100%' }}>
          <tbody>
            <tr>
              <td>⌘ + L</td>
              <td>jump to line</td>
            </tr>
            <tr>
              <td>⌘ + ↑</td>
              <td>jump to the first line</td>
            </tr>
            <tr>
              <td>⌘ + ↓</td>
              <td>jump to the last line</td>
            </tr>
          </tbody>
        </table>
      </div>

      <Snackbar
        open={toastOpen}
        autoHideDuration={5000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="warning" onClose={() => setToastOpen(false)}>
          No &quot;Bug Report Button Tap&quot; event found in this log.
        </Alert>
      </Snackbar>
    </div>
  );
}

interface State {
  isOpen: boolean;
}

export class AppSettingsMenuItem extends React.Component<AppSettingsProps, State> {
  private anchorElement = React.createRef<HTMLLIElement>();

  constructor(props: AppSettingsProps) {
    super(props);

    this.state = {
      isOpen: false,
    };
  }

  render() {
    return [
      <MenuItem
        key="0"
        ref={this.anchorElement}
        onClick={() => {
          this.onClick();
        }}
      >
        <Settings />
      </MenuItem>,
      <Popover
        key="1"
        open={this.state.isOpen}
        anchorEl={this.anchorElement.current}
        onClose={() => {
          this.onClose();
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <div style={{ margin: '8px' }}>
          <AppSettingsDiv
            settingsManager={this.props.settingsManager}
            onNoBugButtonFound={this.props.onNoBugButtonFound}
          />
        </div>
      </Popover>,
    ];
  }

  private onClick() {
    this.setState({ isOpen: true });
  }

  private onClose() {
    this.setState({ isOpen: false });
  }
}
